/* eslint-disable react-native/no-inline-styles */
import React, { useState, useCallback } from 'react';
import {
    View,
    Text,
    StyleSheet,
    ScrollView,
    RefreshControl,
    TouchableOpacity,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useFocusEffect } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { theme } from '../constants/theme';
import { ScreenHeader } from '../components/ScreenHeader';
import ApiService from '../services/ApiService';
import { DashboardStats, DairyRecord, CowRecord } from '../models/types';
import { formatNumber, formatCurrency } from '../utils/helpers';
import {
    SkeletonCard,
    SkeletonOverview,
    SkeletonChart,
    SkeletonExpenseCard,
    SkeletonNetProfit,
} from '../components/ShimmerLoading';
import { SummaryListCard } from '../components/SummaryListCard';
import { NetProfitCard } from '../components/NetProfitCard';

interface DairyTotals {
    purchaseQty: number;
    purchaseAmount: number;
    saleQty: number;
    saleAmount: number;
}

interface DailyMilkData {
    date: string;
    day: string;
    quantity: number;
}

const DashboardScreen = ({ navigation }: any) => {
    const [stats, setStats] = useState<DashboardStats>({
        totalCows: 0,
        activeCows: 0,
        todayMilkProduction: 0,
        monthMilkProduction: 0,
        monthFodderCost: 0,
        monthMedicineCost: 0,
        monthRevenue: 0,
        topProducers: [],
    });
    const [dairyTotals, setDairyTotals] = useState<DairyTotals>({
        purchaseQty: 0,
        purchaseAmount: 0,
        saleQty: 0,
        saleAmount: 0,
    });
    const [weeklyMilkData, setWeeklyMilkData] = useState<DailyMilkData[]>([]);
    const [refreshing, setRefreshing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    const loadStats = async (showLoader = true) => {
        try {
            if (showLoader && isInitialLoad) {
                setLoading(true);
            }
            const data = await ApiService.getDashboardStats();
            setStats(data);

            // Calculate last 7 days date range
            const today = new Date();
            const sevenDaysAgo = new Date();
            sevenDaysAgo.setDate(today.getDate() - 6); // 7 days including today
            const toDateStr = today.toISOString().split('T')[0];
            const fromDateStr = sevenDaysAgo.toISOString().split('T')[0];

            // Load dairy records for totals (filtered by last 7 days)
            const dairyRecords = await ApiService.getDairyRecords();
            const totals: DairyTotals = {
                purchaseQty: 0,
                purchaseAmount: 0,
                saleQty: 0,
                saleAmount: 0,
            };

            // Get unique days from dairy records (within last 7 days)
            const uniqueDays = new Set<string>();
            dairyRecords.forEach((record: DairyRecord) => {
                const recordDate = record.date ? record.date.split('T')[0] : '';
                // Only include records from last 7 days
                if (recordDate >= fromDateStr && recordDate <= toDateStr) {
                    if (record.recordType === 'purchase') {
                        totals.purchaseQty += record.quantity;
                        totals.purchaseAmount += record.totalAmount || 0;
                    } else if (record.recordType === 'sale') {
                        totals.saleQty += record.quantity;
                        totals.saleAmount += record.totalAmount || 0;
                    }
                    // Track unique dates
                    uniqueDays.add(recordDate);
                }
            });

            setDairyTotals(totals);

            // Load 7-day milk production data
            await loadWeeklyMilkData();
        } catch (error) {
            console.error('Error loading dashboard stats:', error);
        } finally {
            setLoading(false);
            setIsInitialLoad(false);
        }
    };

    const loadWeeklyMilkData = async () => {
        try {
            // Get all cows to fetch their milk records
            const cows = await ApiService.getCows();

            // Get last 7 days including today
            const days: DailyMilkData[] = [];
            const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

            for (let i = 6; i >= 0; i--) {
                const date = new Date();
                date.setDate(date.getDate() - i);
                const dateStr = date.toISOString().split('T')[0];
                days.push({
                    date: dateStr,
                    day: dayNames[date.getDay()],
                    quantity: 0,
                });
            }

            // Fetch milk records for each cow and aggregate by day
            for (const cow of cows) {
                const milkRecords = await ApiService.getCowRecords(cow.id, 'milk');
                milkRecords.forEach((record: CowRecord) => {
                    const recordDate = record.date.split('T')[0];
                    const dayIndex = days.findIndex(d => d.date === recordDate);
                    if (dayIndex !== -1) {
                        days[dayIndex].quantity += record.milkQuantity || 0;
                    }
                });
            }

            setWeeklyMilkData(days);
        } catch (error) {
            console.error('Error loading weekly milk data:', error);
        }
    };

    useFocusEffect(
        useCallback(() => {
            loadStats();
        }, []),
    );

    const onRefresh = async () => {
        setRefreshing(true);
        await loadStats();
        setRefreshing(false);
    };

    // Net profit = Sales Revenue - (Fodder + Medicine + Purchase Costs)
    const totalRevenue = dairyTotals.saleAmount;
    const totalExpenses = stats.monthFodderCost + stats.monthMedicineCost + dairyTotals.purchaseAmount;
    const netProfit = totalRevenue - totalExpenses;

    // Calculate remaining milk stock
    const totalMilkAvailable = stats.monthMilkProduction + dairyTotals.purchaseQty;
    const remainingMilk = totalMilkAvailable - dairyTotals.saleQty;

    if (loading && isInitialLoad) {
        return (
            <SafeAreaView style={styles.container}>
                <ScreenHeader
                    title="Dairy Farm Dashboard"
                    showDrawer={true}
                    navigation={navigation}
                />
                <ScrollView
                    style={styles.scrollView}
                    contentContainerStyle={styles.scrollContent}
                    showsVerticalScrollIndicator={false}>
                    <SkeletonOverview />
                    <SkeletonChart />
                    <SkeletonOverview />
                    <SkeletonExpenseCard />
                    <SkeletonCard />
                    <SkeletonNetProfit />
                    <View style={styles.bottomPadding} />
                </ScrollView>
            </SafeAreaView>
        );
    }

    return (
        <SafeAreaView style={styles.container}>
            <ScreenHeader
                title="Dairy Farm Dashboard"
                showDrawer={true}
                navigation={navigation}
            />

            <ScrollView
                style={styles.scrollView}
                contentContainerStyle={styles.scrollContent}
                refreshControl={
                    <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
                }
                showsVerticalScrollIndicator={false}>

                {/* Overview Stats - Side by Side (Total Cows & Active Cows) */}
                <View style={styles.overviewCard}>
                    <TouchableOpacity
                        style={styles.overviewItem}
                        onPress={() => navigation.navigate('Cows')}>
                        <View style={styles.overviewHeader}>
                            <Icon name="cow" size={18} color={theme.colors.primary} />
                            <Text style={styles.overviewLabel}>Total Cows</Text>
                        </View>
                        <Text style={styles.overviewValue}>{stats.totalCows}</Text>
                    </TouchableOpacity>

                    <View style={styles.overviewDivider} />

                    <View style={styles.overviewItem}>
                        <View style={styles.overviewHeader}>
                            <Icon name="check-circle" size={18} color={theme.colors.success} />
                            <Text style={styles.overviewLabel}>Active Cows</Text>
                        </View>
                        <Text style={styles.overviewValue}>{stats.activeCows}</Text>
                    </View>
                </View>

                {/* 7-Day Milk Production Chart */}
                <View style={styles.milkProductionCard}>
                    <Text style={styles.cardTitle}>Milk Production (Last 7 Days)</Text>
                    <View style={styles.milkValueRow}>
                        <Text style={styles.milkValue}>
                            {formatNumber(weeklyMilkData.length > 0 ? weeklyMilkData[weeklyMilkData.length - 1].quantity : 0)}
                        </Text>
                        <Text style={styles.milkUnit}>L</Text>
                        <Text style={styles.todayLabel}>Today</Text>
                    </View>

                    {/* 7-Day Bar Chart */}
                    <View style={styles.chartContainer}>
                        {weeklyMilkData.map((day, index) => {
                            const maxQty = Math.max(...weeklyMilkData.map(d => d.quantity), 1);
                            const barHeight = (day.quantity / maxQty) * 80;
                            const isToday = index === weeklyMilkData.length - 1;
                            return (
                                <View key={day.date} style={styles.chartBarWrapper}>
                                    <Text style={styles.chartValue}>
                                        {day.quantity > 0 ? formatNumber(day.quantity) : ''}
                                    </Text>
                                    <View style={styles.chartBarContainer}>
                                        <View
                                            style={[
                                                styles.chartBar,
                                                {
                                                    height: Math.max(barHeight, 4),
                                                    backgroundColor: isToday ? theme.colors.primary : theme.colors.primary + '60',
                                                }
                                            ]}
                                        />
                                    </View>
                                    <Text style={[styles.chartLabel, isToday && styles.chartLabelToday]}>
                                        {day.day}
                                    </Text>
                                </View>
                            );
                        })}
                    </View>
                </View>

                {/* Last 7 Days Stats - Side by Side */}
                <View style={styles.overviewCard}>
                    <View style={styles.overviewItem}>
                        <View style={styles.overviewHeader}>
                            <Icon name="chart-line" size={18} color={theme.colors.primary} />
                            <Text style={styles.overviewLabel}>7 Days Production</Text>
                        </View>
                        <View style={styles.overviewValueRow}>
                            <Text style={styles.overviewValueLarge}>{formatNumber(stats.monthMilkProduction)}</Text>
                            <Text style={styles.overviewUnit}>L</Text>
                        </View>
                    </View>

                    <View style={styles.overviewDivider} />

                    <View style={styles.overviewItem}>
                        <View style={styles.overviewHeader}>
                            <Icon name="currency-inr" size={18} color={theme.colors.success} />
                            <Text style={styles.overviewLabel}>Revenue</Text>
                        </View>
                        <Text style={[styles.overviewValueLarge, { color: theme.colors.success }]}>
                            {formatCurrency(totalRevenue)}
                        </Text>
                    </View>
                </View>

                {/* Expenses Summary */}
                <SummaryListCard
                    title="Expenses Summary"
                    rightLabel="Last 7 Days"
                    items={[
                        {
                            icon: 'grass',
                            iconColor: theme.colors.feed,
                            iconBgColor: theme.colors.feed + '20',
                            label: 'Fodder Cost',
                            value: formatCurrency(stats.monthFodderCost),
                        },
                        {
                            icon: 'medical-bag',
                            iconColor: theme.colors.medicine,
                            iconBgColor: theme.colors.medicine + '20',
                            label: 'Medicine Cost',
                            value: formatCurrency(stats.monthMedicineCost),
                        },
                        {
                            icon: 'cup',
                            iconColor: theme.colors.primary,
                            iconBgColor: theme.colors.primary + '20',
                            label: 'Milk Production',
                            value: `${formatNumber(stats.monthMilkProduction)} L`,
                        },
                    ]}
                />

                {/* Dairy Records Summary */}
                <SummaryListCard
                    title="Dairy Records"
                    rightLabel="Last 7 Days"
                    onPress={() => navigation.navigate('Customers')}
                    showChevron={true}
                    items={[
                        {
                            icon: 'cart-plus',
                            iconColor: '#FF9800',
                            iconBgColor: '#FFF3E0',
                            label: 'Purchases',
                            value: `${formatNumber(dairyTotals.purchaseQty)} L`,
                            subValue: formatCurrency(dairyTotals.purchaseAmount),
                        },
                        {
                            icon: 'cash-multiple',
                            iconColor: '#4CAF50',
                            iconBgColor: '#E8F5E9',
                            label: 'Sales',
                            value: `${formatNumber(dairyTotals.saleQty)} L`,
                            valueColor: theme.colors.success,
                            subValue: formatCurrency(dairyTotals.saleAmount),
                            subValueColor: theme.colors.success,
                        },
                    ]}
                />

                {/* Remaining Milk Stock Card */}
                <SummaryListCard
                    title="Milk Stock Summary"
                    rightLabel="Last 7 Days"
                    items={[
                        {
                            icon: 'cup',
                            iconColor: theme.colors.primary,
                            iconBgColor: theme.colors.primary + '20',
                            label: 'Produced by Cows',
                            value: `${formatNumber(stats.monthMilkProduction)} L`,
                        },
                        {
                            icon: 'cart-plus',
                            iconColor: '#FF9800',
                            iconBgColor: '#FFF3E0',
                            label: 'Purchased',
                            value: `+${formatNumber(dairyTotals.purchaseQty)} L`,
                        },
                        {
                            icon: 'cart-minus',
                            iconColor: theme.colors.error,
                            iconBgColor: '#FFEBEE',
                            label: 'Sold',
                            value: `-${formatNumber(dairyTotals.saleQty)} L`,
                            valueColor: theme.colors.error,
                        },
                        {
                            icon: 'water',
                            iconColor: remainingMilk >= 0 ? '#2196F3' : theme.colors.error,
                            iconBgColor: remainingMilk >= 0 ? '#E3F2FD' : '#FFEBEE',
                            label: 'Remaining Stock',
                            value: `${formatNumber(remainingMilk)} L`,
                            valueColor: remainingMilk >= 0 ? '#2196F3' : theme.colors.error,
                            labelStyle: { fontWeight: '700' },
                            valueStyle: { fontWeight: '700', fontSize: 16 },
                        },
                    ]}
                />

                {/* Net Profit Summary Card - Prominent Display */}
                <NetProfitCard
                    netProfit={netProfit}
                    totalRevenue={totalRevenue}
                    totalExpenses={totalExpenses}
                    subLabel="Last 7 Days"
                />

                {/* Top Producers Section */}
                {stats.topProducers.length > 0 && (
                    <View style={styles.section}>
                        <Text style={styles.cardTitle}>Top Milk Producers (This Month)</Text>
                        {stats.topProducers.map((producer, index) => (
                            <View key={producer.cowId} style={styles.producerCard}>
                                <View style={styles.rankBadge}>
                                    <Text style={styles.rankText}>{index + 1}</Text>
                                </View>
                                <View style={styles.producerInfo}>
                                    {/* <Text style={styles.producerName}>{producer.name}</Text> */}
                                    <Text style={styles.producerId}>{producer.cowId}</Text>
                                </View>
                                <Text style={styles.producerValue}>
                                    {formatNumber(producer.production)} L
                                </Text>
                            </View>
                        ))}
                    </View>
                )}

                <View style={styles.bottomPadding} />
            </ScrollView>
        </SafeAreaView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background,
    },
    header: {
        backgroundColor: theme.colors.primary,
        paddingVertical: theme.spacing.md,
        paddingHorizontal: theme.spacing.md,
        flexDirection: 'row',
        alignItems: 'center',
    },
    headerTitle: {
        ...theme.typography.h5,
        color: theme.colors.surface,
        fontWeight: '700',
        flex: 1,
        // marginLeft: theme.spacing.sm,
        textAlign: 'center',
    },
    menuButton: {
        padding: theme.spacing.xs,
    },
    scrollView: {
        flex: 1,
    },
    scrollContent: {
        padding: theme.spacing.md,
    },

    // Overview Card - Side by Side Stats
    overviewCard: {
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.xl,
        padding: theme.spacing.md,
        flexDirection: 'row',
        marginBottom: theme.spacing.md,
        ...theme.shadows.sm,
    },
    overviewItem: {
        flex: 1,
        alignItems: 'center',
    },
    overviewHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: theme.spacing.sm,
    },
    overviewLabel: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
        marginLeft: theme.spacing.xs,
    },
    overviewValue: {
        ...theme.typography.h5,
        color: theme.colors.text,
        fontWeight: '700',
    },
    overviewValueRow: {
        flexDirection: 'row',
        alignItems: 'baseline',
    },
    overviewValueLarge: {
        ...theme.typography.h6,
        color: theme.colors.primary,
        fontWeight: '700',
    },
    overviewUnit: {
        ...theme.typography.h5,
        color: theme.colors.primary,
        fontWeight: '600',
        marginLeft: 2,
    },
    overviewDivider: {
        width: 1,
        height: 50,
        backgroundColor: theme.colors.divider,
        marginHorizontal: theme.spacing.md,
    },

    // Milk Production Card
    milkProductionCard: {
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.xl,
        padding: theme.spacing.md,
        marginBottom: theme.spacing.md,
        ...theme.shadows.sm,
    },
    cardTitle: {
        ...theme.typography.body1,
        color: theme.colors.text,
        fontWeight: '700',
        marginBottom: theme.spacing.sm,
    },
    cardTitleRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing.sm,
    },
    dairyValueContainer: {
        alignItems: 'flex-end',
    },
    dairyAmount: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
        marginTop: 2,
    },
    milkValueRow: {
        flexDirection: 'row',
        alignItems: 'baseline',
    },
    milkValue: {
        fontSize: 20,
        fontWeight: '700',
        color: theme.colors.primary,
    },
    milkUnit: {
        ...theme.typography.h4,
        color: theme.colors.primary,
        fontWeight: '600',
        marginLeft: 4,
    },
    todayLabel: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
        marginLeft: theme.spacing.md,
    },
    // 7-Day Chart Styles
    chartContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        marginTop: theme.spacing.lg,
        height: 120,
        paddingTop: theme.spacing.md,
    },
    chartBarWrapper: {
        flex: 1,
        alignItems: 'center',
        marginHorizontal: 2,
    },
    chartValue: {
        ...theme.typography.caption,
        color: theme.colors.primary,
        fontSize: 9,
        fontWeight: '600',
        marginBottom: 4,
        height: 14,
    },
    chartBarContainer: {
        flex: 1,
        width: '100%',
        justifyContent: 'flex-end',
        alignItems: 'center',
    },
    chartBar: {
        width: '80%',
        borderRadius: theme.borderRadius.sm,
        minHeight: 4,
    },
    chartLabel: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
        fontSize: 10,
        marginTop: 6,
    },
    chartLabelToday: {
        color: theme.colors.primary,
        fontWeight: '700',
    },
    graphContainer: {
        height: 40,
        marginTop: theme.spacing.md,
        borderRadius: theme.borderRadius.lg,
        overflow: 'hidden',
        backgroundColor: theme.colors.primary + '10',
    },
    graphLine: {
        position: 'absolute',
        top: 8,
        left: 0,
        right: 0,
        height: 4,
        backgroundColor: theme.colors.primary,
        borderRadius: 2,
    },
    graphArea: {
        flex: 1,
        backgroundColor: theme.colors.primary + '30',
        marginTop: 12,
    },

    // Expenses Card
    expensesCard: {
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.xl,
        padding: theme.spacing.md,
        marginBottom: theme.spacing.md,
        ...theme.shadows.sm,
    },
    expenseItem: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: theme.spacing.sm,
    },
    expenseIconContainer: {
        width: 36,
        height: 36,
        borderRadius: theme.borderRadius.md,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: theme.spacing.md,
    },
    expenseLabel: {
        ...theme.typography.body2,
        color: theme.colors.text,
        flex: 1,
    },
    expenseValue: {
        ...theme.typography.body1,
        color: theme.colors.text,
        fontWeight: '600',
        fontSize: 14,
    },
    expenseDivider: {
        height: 1,
        backgroundColor: theme.colors.divider,
    },

    // Section
    section: {
        marginBottom: theme.spacing.lg,
    },

    // Producer Cards
    producerCard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.xl,
        padding: theme.spacing.md,
        marginBottom: theme.spacing.sm,
        ...theme.shadows.sm,
    },
    rankBadge: {
        width: 32,
        height: 32,
        borderRadius: theme.borderRadius.round,
        backgroundColor: theme.colors.primary + '20',
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: theme.spacing.md,
    },
    rankText: {
        ...theme.typography.body2,
        color: theme.colors.primary,
        fontWeight: '700',
    },
    producerInfo: {
        flex: 1,
    },
    producerName: {
        ...theme.typography.body1,
        color: theme.colors.text,
        fontWeight: '600',
    },
    producerId: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
        marginTop: 2,
    },
    producerValue: {
        ...theme.typography.body1,
        color: theme.colors.primary,
        fontWeight: '700',
    },
    bottomPadding: {
        height: theme.spacing.xl,
    },
    // Days Indicator
    daysIndicator: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
        backgroundColor: theme.colors.background,
        paddingHorizontal: theme.spacing.sm,
        paddingVertical: theme.spacing.xs,
        borderRadius: theme.borderRadius.md,
        fontSize: 11,
        fontWeight: '600',
    },
    cardTitleRightSection: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing.sm,
    },
    // Net Profit Card Styles
    netProfitCard: {
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.xl,
        padding: theme.spacing.md,
        marginBottom: theme.spacing.md,
        ...theme.shadows.md,
        borderWidth: 1,
        borderColor: theme.colors.divider,
    },
    netProfitHeader: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    netProfitIconContainer: {
        width: 48,
        height: 48,
        borderRadius: 24,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: theme.spacing.md,
    },
    netProfitContent: {
        flex: 1,
    },
    netProfitLabel: {
        ...theme.typography.body1,
        color: theme.colors.text,
        fontWeight: '700',
    },
    netProfitSubLabel: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
        marginTop: 2,
    },
    netProfitValue: {
        ...theme.typography.h5,
        fontWeight: '800',
    },
    netProfitBreakdown: {
        flexDirection: 'row',
        marginTop: theme.spacing.md,
        paddingTop: theme.spacing.md,
        borderTopWidth: 1,
        borderTopColor: theme.colors.divider,
    },
    netProfitBreakdownItem: {
        flex: 1,
        alignItems: 'center',
    },
    netProfitBreakdownDivider: {
        width: 1,
        height: 30,
        backgroundColor: theme.colors.divider,
    },
    breakdownLabel: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
        marginBottom: 4,
    },
    breakdownValue: {
        ...theme.typography.body2,
        fontWeight: '700',
    },
});


export default DashboardScreen;
